import dayjs, { Dayjs } from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export const parseCommitmentDate = (dateString: string): Dayjs | null => {
  try {
    if (!dateString || dateString === 'Pending' || dateString === 'No due date') return null;
    if (dateString === 'Today') return dayjs().startOf('day');
    
    let cleanDateString = dateString.trim();
    
    // Remove common prefixes
    if (cleanDateString.startsWith('Due ')) {
      cleanDateString = cleanDateString.substring('Due '.length);
    }
    if (cleanDateString.startsWith('Completed ')) {
      cleanDateString = cleanDateString.substring('Completed '.length);
    }
    
    // First try parsing with specific formats (for display strings)
    let date = dayjs(cleanDateString, ['MMM D, hh:mm A', 'MMM D, YYYY, hh:mm A', 'MMM D', 'MMM D, YYYY'], true);
    
    // If that fails, try parsing as ISO or any format dayjs can handle (for API dates)
    if (!date.isValid()) {
      date = dayjs(cleanDateString);
    }
    
    return date.isValid() ? date : null;
  } catch {
    return null;
  }
};

export const parseCommittedDate = (dateString?: string): Dayjs | null => {
  if (!dateString) return null;

  try {
    let cleanDateString = dateString
      .replace(/^Committed\s+/i, "")
      .replace(/^Requested on\s+/i, "")
      .trim();
    let date: Dayjs | null = dayjs(cleanDateString, ['MMM D, hh:mm A', 'MMM D, YYYY, hh:mm A', 'MMM D, YYYY', 'MMM D'], true);
    if (!date.isValid()) {
      date = parseCommitmentDate(cleanDateString);
    }
    return date && date.isValid() ? date : null;
  } catch {
    return null;
  }
};

/**
 * Convert API ISO-like timestamps to "YYYY-MM-DD HH:mm" for UI display.
 * Leaves non-date labels (e.g., "No due date", "Pending") unchanged.
 */
export const formatDateTimeCompact = (
  value?: string | null,
  fallback: string = "No date"
): string => {
  if (value == null) return fallback;
  const raw = String(value).trim();
  if (!raw) return fallback;
  if (
    raw === "No due date" ||
    raw === "No date" ||
    raw === "Pending" ||
    raw === "N/A"
  ) {
    return raw;
  }
  const parsed = dayjs(raw);
  if (!parsed.isValid()) {
    return raw;
  }
  return parsed.format("YYYY-MM-DD HH:mm");
};

/** Epoch ms for notification received/due columns (invalid → sort last). */
export const epochForSortNotificationDate = (
  raw: string | undefined | null
): number => {
  if (!raw) return Number.MAX_SAFE_INTEGER;
  const s = String(raw).trim();
  if (!s || s === "No date" || s === "No due date" || s === "Pending") {
    return Number.MAX_SAFE_INTEGER;
  }
  const parsed = dayjs(s);
  return parsed.isValid() ? parsed.valueOf() : Number.MAX_SAFE_INTEGER;
};

/** Epoch ms for sorting; missing/invalid dates sort last. */
export const epochForSortDue = (raw: string | undefined): number => {
  const d = raw ? parseCommitmentDate(raw) : null;
  return d && d.isValid() ? d.valueOf() : Number.MAX_SAFE_INTEGER;
};

export const epochForSortCommitted = (raw: string | undefined): number => {
  const d = raw ? parseCommittedDate(raw) : null;
  if (d && d.isValid()) return d.valueOf();
  const fallback = raw ? parseCommitmentDate(raw.replace(/^Requested on\s+/i, "").trim()) : null;
  return fallback && fallback.isValid() ? fallback.valueOf() : Number.MAX_SAFE_INTEGER;
};

export const epochForSortApproved = (raw: string | undefined): number => {
  if (!raw) return Number.MAX_SAFE_INTEGER;
  const stripped = raw.replace(/^Approved\s+/i, "").trim();
  const d = parseCommitmentDate(stripped);
  return d && d.isValid() ? d.valueOf() : Number.MAX_SAFE_INTEGER;
};

export const formatDateRange = (start: Dayjs | null, end: Dayjs | null): string => {
  if (!start && !end) return 'All';
  if (start && !end) return `From ${start.format('MMM D, YYYY')}`;
  if (!start && end) return `Until ${end.format('MMM D, YYYY')}`;
  return `${start?.format('MMM D, YYYY')} - ${end?.format('MMM D, YYYY')}`;
};

export const isDateInRange = (date: Dayjs | null, range: [Dayjs | null, Dayjs | null]): boolean => {
  if (!date) return false;
  const [start, end] = range;
  
  // Normalize dates to start of day for accurate comparison
  const dateStart = date.startOf('day');
  const startNormalized = start ? start.startOf('day') : null;
  const endNormalized = end ? end.startOf('day') : null; // Normalize to start of day for consistent comparison
  
  if (!startNormalized && !endNormalized) return true;
  if (startNormalized && !endNormalized) {
    return dateStart.isSameOrAfter(startNormalized, 'day');
  }
  if (!startNormalized && endNormalized) {
    return dateStart.isSameOrBefore(endNormalized, 'day');
  }
  
  // Both start and end are set - check if date is in range (inclusive)
  if (startNormalized && endNormalized) {
    return dateStart.isSameOrAfter(startNormalized, 'day') && 
           dateStart.isSameOrBefore(endNormalized, 'day');
  }
  
  return false;
};
